import { FieldType, FieldDataType } from '@/modules/to/formbuilder/field.constants';

/**
 * Sentinel value used in dropdowns to represent the "Create new..." option.
 * Used in WorkflowNodePanel and AdvancedSettings.
 */
// eslint-disable-next-line tap/no-raw-text-js
export const CREATE_NEW_SENTINEL = '__create_new__';

// Layout dimensions (in SVG units, ~px)
export const Layout = Object.freeze({
  NODE_WIDTH: 220,
  NODE_HEIGHT: 110,
  COLUMN_GAP: 180,
  ROW_GAP: 40,
  CONDITION_SIZE: 80,
  ENTRY_RADIUS: 8,
  EXTERNAL_RADIUS: 8,
  CANVAS_PADDING: 60,
  GRID_SIZE: 20,
  TASK_LINE_HEIGHT: 18,
  MAX_VISIBLE_TASK_LABELS: 3,
  TASK_FOOTER_TOP: 4,
  TASK_FOOTER_BOTTOM: 4,
});

// eslint-disable-next-line tap/no-raw-text-js
export const SystemActionKey = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  ENTITY_CREATED: 'entity_created',
  // eslint-disable-next-line tap/no-raw-text-js
  ENTITY_UPDATED: 'entity_updated',
  // eslint-disable-next-line tap/no-raw-text-js
  ENTITY_STATUS_CHANGED: 'entity_status_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  COMPLETED: 'completed',
  // eslint-disable-next-line tap/no-raw-text-js
  CANCELLED: 'cancelled',
  // eslint-disable-next-line tap/no-raw-text-js
  COMMENT_ADDED: 'comment_added',
});

// eslint-disable-next-line tap/no-raw-text-js
export const SharedActionKey = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  APPROVE: 'approve',
  // eslint-disable-next-line tap/no-raw-text-js
  REJECT: 'reject',
});

// eslint-disable-next-line tap/no-raw-text-js
export const ActionScope = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  SYSTEM: 'system',
  // eslint-disable-next-line tap/no-raw-text-js
  SHARED: 'shared',
  // eslint-disable-next-line tap/no-raw-text-js
  CUSTOM: 'custom',
});

// eslint-disable-next-line tap/no-raw-text-js
export const CustomType = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  INTERNAL: 'internal',
  // eslint-disable-next-line tap/no-raw-text-js
  UP: 'up',
  // eslint-disable-next-line tap/no-raw-text-js
  DOWN: 'down',
  // eslint-disable-next-line tap/no-raw-text-js
  EDIT: 'edit',
});

// eslint-disable-next-line tap/no-raw-text-js
export const OutcomeType = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  SET_STATUS: 'set_status',
  // eslint-disable-next-line tap/no-raw-text-js
  SET_FIELD_VALUE: 'set_field_value',
  // eslint-disable-next-line tap/no-raw-text-js
  CREATE_TASK: 'create_task',
  // eslint-disable-next-line tap/no-raw-text-js
  AUXILIARY_ACTION: 'auxiliary_action',
  // eslint-disable-next-line tap/no-raw-text-js
  PUSH: 'push',
  // eslint-disable-next-line tap/no-raw-text-js
  COMPLETE_TASK: 'complete_task',
});

// eslint-disable-next-line tap/no-raw-text-js
export const AuxDirection = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  SELF: 'self',
  // eslint-disable-next-line tap/no-raw-text-js
  UP: 'up',
  // eslint-disable-next-line tap/no-raw-text-js
  DOWN: 'down',
});

export const OutcomeTypeIcon = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  [OutcomeType.SET_FIELD_VALUE]: 'ti-pencil',
  // eslint-disable-next-line tap/no-raw-text-js
  [OutcomeType.CREATE_TASK]: 'ti-list-to-do',
  // eslint-disable-next-line tap/no-raw-text-js
  [OutcomeType.PUSH]: 'ti-send-email',
  // eslint-disable-next-line tap/no-raw-text-js
  [OutcomeType.SET_STATUS]: 'ti-synchronize-arrows-1',
  // eslint-disable-next-line tap/no-raw-text-js
  [OutcomeType.AUXILIARY_ACTION]: 'ti-share-2',
  // eslint-disable-next-line tap/no-raw-text-js
  [OutcomeType.COMPLETE_TASK]: 'ti-check-circle-2',
});

export const INITIAL_STATUS_FIELD_MAP = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  client: 'initial_client_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  order: 'initial_order_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  line_item: 'initial_product_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  flight: 'initial_flight_status_id',
});

export const STATUS_FIELD_MAP = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  client: 'client_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  order: 'order_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  line_item: 'product_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  flight: 'flight_status_id',
});

export const NEW_STATUS_FIELD_MAP = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  client: 'new_client_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  order: 'new_order_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  line_item: 'new_product_status_id',
  // eslint-disable-next-line tap/no-raw-text-js
  flight: 'new_flight_status_id',
});

// eslint-disable-next-line tap/no-raw-text-js
export const ValueSource = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  CONSTANT: 'constant',
  // eslint-disable-next-line tap/no-raw-text-js
  FORM_FIELD: 'form_field',
  // eslint-disable-next-line tap/no-raw-text-js
  PARENT_FORM_FIELD: 'parent_form_field',
});

// eslint-disable-next-line tap/no-raw-text-js
export const TaskPriority = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  HIGH: 'high',
  // eslint-disable-next-line tap/no-raw-text-js
  MEDIUM: 'medium',
  // eslint-disable-next-line tap/no-raw-text-js
  LOW: 'low',
});

// eslint-disable-next-line tap/no-raw-text-js
export const DueDateSource = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  NONE: 'none',
  // eslint-disable-next-line tap/no-raw-text-js
  CURRENT_DATE: 'current_date',
  // eslint-disable-next-line tap/no-raw-text-js
  FORM_FIELD: 'form_field',
});

// eslint-disable-next-line tap/no-raw-text-js
export const AssigneeSource = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  NONE: 'none',
  // eslint-disable-next-line tap/no-raw-text-js
  FORM_FIELD: 'form_field',
  // eslint-disable-next-line tap/no-raw-text-js
  CONSTANT: 'constant',
  // eslint-disable-next-line tap/no-raw-text-js
  ACCOUNT_MANAGER: 'account_manager',
  // eslint-disable-next-line tap/no-raw-text-js
  CURRENT: 'current',
  // eslint-disable-next-line tap/no-raw-text-js
  TASK_ASSIGNEE: 'task_assignee',
});

// Field types that cannot be set by a workflow outcome (computed/structural fields)
export const NON_SETTABLE_FIELD_TYPES = new Set([
  FieldType.CALCULATION,
  FieldType.ROLLUP_TARGET,
  FieldType.DISTRIBUTED_TARGET,
  FieldType.RATE_CARD,
  FieldType.SELECT_RATE_CARD,
  FieldType.FILE_UPLOAD,
  FieldType.GEN_AI_IMAGE_UPLOAD,
  FieldType.FREQUENCY_CAP,
  FieldType.GEO_TARGETING,
]);

/* eslint-disable tap/no-raw-text-js */
const DATA_TYPE_GROUPS = {
  [FieldDataType.INTEGER]: 'numeric',
  [FieldDataType.PERCENT]: 'numeric',
  [FieldDataType.CURRENCY]: 'numeric',
  [FieldDataType.DECIMAL]: 'numeric',
  [FieldDataType.STRING]: 'string',
  [FieldDataType.PHONENUMBER]: 'string',
  [FieldDataType.ENUM]: 'string',
  [FieldDataType.DATE]: 'date',
  [FieldDataType.TIME]: 'time',
  [FieldDataType.DATETIME]: 'date',
  [FieldDataType.BOOL]: 'boolean',
  [FieldDataType.ARRAY]: 'array',
};
/* eslint-enable tap/no-raw-text-js */

/**
 * Maps a field data type to a broad compatibility group.
 * @param {string} dataType
 * @returns {string}
 */
export function getDataTypeGroup(dataType) {
  // eslint-disable-next-line tap/no-raw-text-js
  return DATA_TYPE_GROUPS[dataType] || 'complex';
}

// --- Condition tree constants ---

// eslint-disable-next-line tap/no-raw-text-js
export const ConditionType = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  GROUP: 'group',
  // eslint-disable-next-line tap/no-raw-text-js
  COMPARISON: 'comparison',
});

// eslint-disable-next-line tap/no-raw-text-js
export const LogicalOperator = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  AND: 'and',
  // eslint-disable-next-line tap/no-raw-text-js
  OR: 'or',
});

// eslint-disable-next-line tap/no-raw-text-js
export const ComparisonOperator = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  EQ: 'eq',
  // eslint-disable-next-line tap/no-raw-text-js
  NEQ: 'neq',
  // eslint-disable-next-line tap/no-raw-text-js
  GT: 'gt',
  // eslint-disable-next-line tap/no-raw-text-js
  GTE: 'gte',
  // eslint-disable-next-line tap/no-raw-text-js
  LT: 'lt',
  // eslint-disable-next-line tap/no-raw-text-js
  LTE: 'lte',
  // eslint-disable-next-line tap/no-raw-text-js
  EMPTY: 'empty',
  // eslint-disable-next-line tap/no-raw-text-js
  NOT_EMPTY: 'not_empty',
  // eslint-disable-next-line tap/no-raw-text-js
  HAS_CHANGED: 'has_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  HAS_NOT_CHANGED: 'has_not_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  CONTAINS: 'contains',
  // eslint-disable-next-line tap/no-raw-text-js
  NOT_CONTAINS: 'not_contains',
});

/* eslint-disable tap/no-raw-text-js */
export const OPERATOR_LABELS = Object.freeze({
  [ComparisonOperator.EQ]: '=',
  [ComparisonOperator.NEQ]: '\u2260',
  [ComparisonOperator.GT]: '>',
  [ComparisonOperator.GTE]: '\u2265',
  [ComparisonOperator.LT]: '<',
  [ComparisonOperator.LTE]: '\u2264',
  [ComparisonOperator.EMPTY]: 'is empty',
  [ComparisonOperator.NOT_EMPTY]: 'is not empty',
  [ComparisonOperator.HAS_CHANGED]: 'has changed',
  [ComparisonOperator.HAS_NOT_CHANGED]: 'has not changed',
  [ComparisonOperator.CONTAINS]: 'contains',
  [ComparisonOperator.NOT_CONTAINS]: 'does not contain',
});
/* eslint-enable tap/no-raw-text-js */

export const UNARY_OPERATORS = new Set([
  ComparisonOperator.EMPTY,
  ComparisonOperator.NOT_EMPTY,
  ComparisonOperator.HAS_CHANGED,
  ComparisonOperator.HAS_NOT_CHANGED,
]);

export const EQUALITY_OPERATORS = [ComparisonOperator.EQ, ComparisonOperator.NEQ];

export const ORDERING_OPERATORS = [
  ComparisonOperator.GT,
  ComparisonOperator.GTE,
  ComparisonOperator.LT,
  ComparisonOperator.LTE,
];

export const CONTAINMENT_OPERATORS = [ComparisonOperator.CONTAINS, ComparisonOperator.NOT_CONTAINS];

// eslint-disable-next-line tap/no-raw-text-js
export const OperandSource = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  FORM_FIELD: 'form_field',
  // eslint-disable-next-line tap/no-raw-text-js
  PARENT_FORM_FIELD: 'parent_form_field',
});

// --- Execution log constants ---

// eslint-disable-next-line tap/no-raw-text-js
export const ExecutionLogType = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  NODE_EXECUTION: 'node_execution',
  // eslint-disable-next-line tap/no-raw-text-js
  TASK_EVENT: 'task_event',
  // eslint-disable-next-line tap/no-raw-text-js
  MANUAL_STATUS_CHANGE: 'manual_status_change',
  // eslint-disable-next-line tap/no-raw-text-js
  CHILDREN_ALL_TERMINAL: 'children_all_terminal',
  // eslint-disable-next-line tap/no-raw-text-js
  PARENT_TERMINAL_PROPAGATION: 'parent_terminal_propagation',
});

// eslint-disable-next-line tap/no-raw-text-js
export const TaskEventType = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  CREATED: 'created',
  // eslint-disable-next-line tap/no-raw-text-js
  APPROVED: 'approved',
  // eslint-disable-next-line tap/no-raw-text-js
  REJECTED: 'rejected',
  // eslint-disable-next-line tap/no-raw-text-js
  COMPLETED: 'completed',
  // eslint-disable-next-line tap/no-raw-text-js
  ASSIGNED: 'assigned',
  // eslint-disable-next-line tap/no-raw-text-js
  UNASSIGNED: 'unassigned',
  // eslint-disable-next-line tap/no-raw-text-js
  REASSIGNED: 'reassigned',
  // eslint-disable-next-line tap/no-raw-text-js
  PRIORITY_CHANGED: 'priority_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  DUE_DATE_CHANGED: 'due_date_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  STATUS_CHANGED: 'status_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  DELETED: 'deleted',
  // eslint-disable-next-line tap/no-raw-text-js
  QUEUE_CHANGED: 'queue_changed',
  // eslint-disable-next-line tap/no-raw-text-js
  FIELDS_UPDATED: 'fields_updated',
  // eslint-disable-next-line tap/no-raw-text-js
  INTERLOCKING_GATE_CHECK: 'interlocking_gate_check',
});

export const TaskEventTypeIcon = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.CREATED]: 'ti-add',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.APPROVED]: 'ti-check-circle',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.REJECTED]: 'ti-close-circle',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.COMPLETED]: 'ti-check-circle-2',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.ASSIGNED]: 'ti-user',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.UNASSIGNED]: 'ti-unassign',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.REASSIGNED]: 'ti-user',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.PRIORITY_CHANGED]: 'ti-Priority-Flag',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.DUE_DATE_CHANGED]: 'ti-calendar',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.STATUS_CHANGED]: 'ti-synchronize-arrows-1',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.DELETED]: 'ti-trash',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.QUEUE_CHANGED]: 'ti-kanban',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.FIELDS_UPDATED]: 'ti-pencil',
  // eslint-disable-next-line tap/no-raw-text-js
  [TaskEventType.INTERLOCKING_GATE_CHECK]: 'ti-synchronize-arrows-1',
});

// eslint-disable-next-line tap/no-raw-text-js
export const InterlockingResolution = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  APPROVE: 'approve',
  // eslint-disable-next-line tap/no-raw-text-js
  REJECT: 'reject',
});

// eslint-disable-next-line tap/no-raw-text-js
export const InterlockingGateResult = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  WAITING: 'waiting',
  // eslint-disable-next-line tap/no-raw-text-js
  APPROVE: 'approve',
  // eslint-disable-next-line tap/no-raw-text-js
  REJECT: 'reject',
});

// eslint-disable-next-line tap/no-raw-text-js
export const InterlockingTaskStatus = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  COMPLETED: 'completed',
  // eslint-disable-next-line tap/no-raw-text-js
  PENDING: 'pending',
});

// eslint-disable-next-line tap/no-raw-text-js
export const WorkflowRoutes = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  WORKFLOW_EXECUTION: 'workflow-execution',
});

// eslint-disable-next-line tap/no-raw-text-js
export const RunEndedReason = Object.freeze({
  // eslint-disable-next-line tap/no-raw-text-js
  RESET: 'reset',
});
